import PropTypes from 'prop-types'
import React from 'react'

import { AddressCardIcon } from '@/components/icons'
import { configureDealerAddress, formatPhoneNumber } from '@/utils/helpers'

export const ContactDetailsSection = ({ dealer, isROServiceUser, loading = false }) => {
  const hasValidDetails = () => {
    if (!dealer) return false

    // If the user is a dealer user, check if admin details are present
    if (isROServiceUser) {
      return dealer.admin || dealer.adminPhoneNumber || dealer.adminEmail
    }

    // Check if dealer contact details are present
    return (
      dealer.address || dealer.address2 || !!configureDealerAddress(dealer) || dealer.phoneNumber
    )
  }

  const renderSkeleton = () => {
    const isAdminLayout = isROServiceUser && !dealer?.isDealerManaged

    if (isAdminLayout) {
      return (
        <div className="flex max-w-fit flex-col items-end md:max-w-full">
          <div className="flex w-full flex-row flex-wrap items-center justify-between gap-2">
            <AddressCardIcon className="w-10 shrink-0" />

            <div className="mt-1 flex flex-col items-end gap-2">
              <div className="bg-charcoal-200 h-3 w-28 animate-pulse rounded-sm"></div>
              <div className="bg-charcoal-200 h-3 w-28 animate-pulse rounded-sm"></div>
            </div>
          </div>

          <div className="bg-charcoal-200 mt-3 h-3 w-40 animate-pulse rounded-sm"></div>
        </div>
      )
    }

    return (
      <div className="flex flex-row items-start justify-end gap-2">
        <AddressCardIcon className="w-10" />

        <div className="mt-1 flex flex-col items-end gap-2">
          <div className="bg-charcoal-200 h-3 w-40 animate-pulse rounded-sm"></div>
          <div className="bg-charcoal-200 h-3 w-28 animate-pulse rounded-sm"></div>
          <div className="bg-charcoal-200 h-3 w-28 animate-pulse rounded-sm"></div>
        </div>
      </div>
    )
  }

  const renderAdminDetails = () => (
    <div className="flex max-w-fit flex-col items-end md:max-w-full">
      <div className="flex w-full flex-row flex-wrap items-center justify-between gap-2">
        <AddressCardIcon className="w-10 shrink-0" />

        <div className="flex flex-col items-end">
          {dealer?.admin && (
            <span className="text-charcoal-900 text-sm font-medium">{dealer.admin}</span>
          )}
          {dealer?.adminPhoneNumber && (
            <a
              href={`tel:${dealer.adminPhoneNumber}`}
              aria-label={`Call ${dealer.admin} at ${formatPhoneNumber(dealer.adminPhoneNumber)}`}
              className="hover:text-blue-dark text-sm font-medium text-blue-800"
            >
              {formatPhoneNumber(dealer.adminPhoneNumber)}
            </a>
          )}
        </div>
      </div>

      {dealer?.adminEmail && (
        <a
          href={`mailto:${dealer.adminEmail}`}
          aria-label={`Email ${dealer.admin} at ${dealer.adminEmail}`}
          className="hover:text-blue-dark w-full whitespace-normal break-words text-right text-sm font-medium text-blue-800"
        >
          {dealer.adminEmail}
        </a>
      )}
    </div>
  )

  const renderDealerDetails = () => (
    <div className="flex flex-row items-start justify-end gap-2">
      <AddressCardIcon className="w-10" />

      <div className="text-charcoal-900 flex flex-col items-end text-sm font-medium">
        {dealer?.address && <span>{dealer.address}</span>}
        {dealer?.address2 && <span>{dealer.address2}</span>}
        {configureDealerAddress(dealer) && <span>{configureDealerAddress(dealer)}</span>}
        {dealer?.phoneNumber && (
          <a
            href={`tel:${dealer.phoneNumber}`}
            aria-label={`Call ${dealer.name} at ${formatPhoneNumber(dealer.phoneNumber)}`}
            className="hover:text-blue-dark font-medium text-blue-800"
          >
            {formatPhoneNumber(dealer.phoneNumber)}
          </a>
        )}
      </div>
    </div>
  )

  return (
    <div className="flex justify-end">
      {loading || !hasValidDetails() ? (
        renderSkeleton()
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {isROServiceUser && !dealer.isDealerManaged
            ? renderAdminDetails()
            : renderDealerDetails()}
        </>
      )}
    </div>
  )
}

ContactDetailsSection.propTypes = {
  dealer: PropTypes.object,
  isROServiceUser: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
}
